import React, { useState, useCallback } from 'react';
import { createCheckoutGuest } from '../../services/api/onboardingService';
import sample1 from '../../assets/onboarding-samples/sample-1.jpg';
import sample2 from '../../assets/onboarding-samples/sample-2.jpg';
import sample3 from '../../assets/onboarding-samples/sample-3.jpg';

// このツールで実際に生成したバナー例（松尾さんのデザイン確定時に差し替え可）
const SAMPLE_BANNERS = [sample1, sample2, sample3];

interface PlanSelectionStepProps {
  email?: string | null;
  leadId?: number | null;
  onSkip: () => void;
}

// Plan definitions
interface PlanDef {
  id: string;
  name: string;
  price: string;
  priceNum: number;
  priceId: string;
  monthlyBanners: string; // 月間生成目安（最も目立つ価値訴求）
  tagline: string; // 誰向け・何が増えるかを1行で
  features: string[];
  recommended: boolean;
}

const PLANS: PlanDef[] = [
  {
    id: 'personal',
    name: 'Personal',
    price: '9,800',
    priceNum: 9800,
    priceId: 'price_1SpAeoQfqRwtOpAJ8tN6d4JQ',
    monthlyBanners: '約180枚',
    tagline: '個人で広告を量産したい方に',
    features: [
      'バナー無制限生成',
      '高解像度DL',
      '全スタイル利用可能',
      'AIチャットサポート',
    ],
    recommended: true,
  },
  {
    id: 'team',
    name: 'Team',
    price: '29,800',
    priceNum: 29800,
    priceId: 'price_1SpAeIQfqRwtOpAJY5mfjids',
    monthlyBanners: '約650枚',
    tagline: 'チームで共有しながら使いたい方に',
    features: [
      'Personal全機能',
      'チーム共有(5名まで)',
      'ブランドガイドライン管理',
      '優先サポート',
    ],
    recommended: false,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '98,000',
    priceNum: 98000,
    priceId: 'price_1SpAfDQfqRwtOpAJtJ1rwGnz',
    monthlyBanners: '約1,100枚',
    tagline: '代理店・大量制作の現場に',
    features: [
      'Team全機能',
      '無制限メンバー',
      'APIアクセス',
      '専任サポート',
      'カスタムスタイル',
    ],
    recommended: false,
  },
];

// --- Tailwind class constants (旧 styled-components) ---
// 旧 keyframes `pulse` は globals.css の `obs-pulse-scale` へ移設。
// 旧 keyframes `fadeInUp` は tailwind.config の同内容既存定義 `animate-fade-in-up` を利用。

const CONTAINER = 'text-center py-4 animate-fade-in-up';

const TITLE = 'text-[1.5rem] font-bold text-gray-800 m-0 mb-2 max-[480px]:text-[1.25rem]';

const SUBTITLE = 'text-[0.9375rem] leading-[1.5] text-gray-500 m-0 mb-6';

const PLANS_GRID =
  'grid grid-cols-3 gap-4 mb-6 ' +
  'max-[768px]:grid-cols-1 max-[768px]:max-w-[400px] max-[768px]:mx-auto';

const PLAN_CARD_RECOMMENDED =
  'border-2 border-violet-600 rounded-xl py-6 px-5 relative bg-white ' +
  'transition-[border-color,box-shadow] duration-200 ease-[ease] flex flex-col ' +
  'shadow-[0_4px_20px_rgba(124,58,237,0.15)] ' +
  'hover:border-violet-600 hover:shadow-[0_4px_20px_rgba(124,58,237,0.15)]';

const PLAN_CARD =
  'border-2 border-gray-200 rounded-xl py-6 px-5 relative bg-white ' +
  'transition-[border-color,box-shadow] duration-200 ease-[ease] flex flex-col ' +
  'hover:border-violet-600 hover:shadow-[0_4px_20px_rgba(124,58,237,0.15)]';

const RECOMMENDED_BADGE =
  'absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white ' +
  'text-[0.6875rem] font-bold py-1 px-4 rounded-[20px] whitespace-nowrap';

const PLAN_NAME = 'text-[1.125rem] font-bold text-gray-700 mb-1 mt-2';

const PRICE_ROW = 'flex items-baseline justify-center gap-0.5 mb-4';

const PRICE = 'text-[2rem] font-bold text-gray-800';

const PRICE_SUFFIX = 'text-[0.875rem] text-gray-500';

// 誰向け・何が増えるかを1行で（#5）
const TAGLINE = 'text-[0.8125rem] text-gray-500 mb-3 min-h-[1.2em]';

// 月間生成枚数を最も目立つ価値として強調（#5）
const BANNER_COUNT_BOX =
  'bg-[linear-gradient(135deg,#f5f3ff_0%,#ede9fe_100%)] rounded-[10px] py-3 px-2 mb-4';

const BANNER_COUNT_NUMBER = 'text-[2rem] leading-[1.1] font-extrabold text-violet-700';

const BANNER_COUNT_LABEL = 'text-[0.75rem] text-violet-600 font-semibold mt-0.5';

// バナー実例サムネ枠（画像は後で差し替え／#5）
const SAMPLE_THUMBS = 'grid grid-cols-3 gap-1.5 mb-4';

const SAMPLE_THUMB =
  'w-full aspect-square object-cover rounded-md block ' +
  'bg-[linear-gradient(135deg,#ede9fe_0%,#dbeafe_100%)]';

// CTA近くの安心材料（#6）
const GUARANTEE_NOTE = 'text-[0.6875rem] text-gray-500 mt-1.5';

// 最上部の「7日間無料」帯（見出し級／#6）
const TRIAL_BANNER =
  'inline-flex items-center gap-2 bg-[linear-gradient(135deg,#14b8a6_0%,#0d9488_100%)] ' +
  'text-white text-[1rem] font-bold py-2.5 px-6 rounded-full mx-auto mt-0 mb-5 ' +
  'shadow-[0_4px_14px_rgba(20,184,166,0.3)]';

const FEATURE_LIST = 'list-none p-0 m-0 mb-5 text-left grow';

// `obs-feature-check` は globals.css（::before のチェックアイコン／mask-image）
const FEATURE_ITEM =
  'obs-feature-check flex items-center gap-2 text-[0.8125rem] text-gray-700 py-1';

const TRIAL_BUTTON_BASE =
  'w-full py-3 px-4 text-[0.9375rem] font-semibold border-2 border-violet-600 rounded-lg ' +
  'transition-all duration-200 ease-[ease] enabled:hover:-translate-y-px ' +
  'enabled:hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)] enabled:active:translate-y-0 ' +
  'enabled:active:shadow-none enabled:active:animate-none disabled:animate-none';

const TRIAL_BUTTON_RECOMMENDED =
  'text-white enabled:hover:bg-violet-700 animate-[obs-pulse-scale_2s_ease-in-out_infinite]';

const TRIAL_BUTTON_PLAIN = 'text-violet-600 enabled:hover:bg-violet-50';

function trialButtonClass(recommended: boolean, loading: boolean): string {
  const variant = recommended ? TRIAL_BUTTON_RECOMMENDED : TRIAL_BUTTON_PLAIN;
  const bg = loading
    ? (recommended ? 'bg-violet-400' : 'bg-violet-50')
    : (recommended ? 'bg-violet-600' : 'bg-white');
  const cursor = loading ? 'cursor-wait' : 'cursor-pointer';
  return `${TRIAL_BUTTON_BASE} ${variant} ${bg} ${cursor}`;
}

const TRIAL_INFO =
  'flex items-center justify-center gap-1.5 text-[0.75rem] text-gray-500 mb-4';

const SHIELD_ICON =
  'inline-flex w-4 h-4 [&_svg]:w-4 [&_svg]:h-4 [&_svg]:fill-teal-500';

const SKIP_LINK =
  'bg-transparent bg-none border-0 text-gray-400 text-[0.75rem] cursor-pointer p-2 mt-2 ' +
  'transition-[color] duration-200 ease-[ease] hover:text-gray-500 hover:underline';

const FOOTER_LINKS = 'flex justify-center gap-4 mt-3';

const FOOTER_LINK =
  'text-[0.6875rem] text-gray-400 no-underline transition-[color] duration-200 ease-[ease] ' +
  'hover:text-violet-600 hover:underline';

// --- Component ---

export const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({
  email,
  leadId,
  onSkip,
}) => {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const handleSelectPlan = useCallback(async (plan: PlanDef) => {
    if (loadingPlanId) return;
    setLoadingPlanId(plan.id);

    try {
      const { checkoutUrl } = await createCheckoutGuest({
        email: email ?? null,
        lead_id: leadId ?? null,
        price_id: plan.priceId,
      });
      window.location.href = checkoutUrl;
    } catch {
      // Reset loading on error
      setLoadingPlanId(null);
    }
  }, [loadingPlanId, email, leadId]);

  return (
    <div className={CONTAINER}>
      <div className={TRIAL_BANNER}>🎁 今なら7日間無料 — 今日は¥0、いつでも解約OK</div>
      <h2 className={TITLE}>プランを選択してください</h2>
      <p className={SUBTITLE}>
        作れる枚数で選んでください
      </p>

      <div className={PLANS_GRID}>
        {PLANS.map((plan) => (
          <div key={plan.id} className={plan.recommended ? PLAN_CARD_RECOMMENDED : PLAN_CARD}>
            {plan.recommended && (
              <div className={RECOMMENDED_BADGE}>おすすめ</div>
            )}
            <div className={PLAN_NAME}>{plan.name} プラン</div>
            <div className={TAGLINE}>{plan.tagline}</div>
            <div className={BANNER_COUNT_BOX}>
              <div className={BANNER_COUNT_NUMBER}>{plan.monthlyBanners}</div>
              <div className={BANNER_COUNT_LABEL}>のバナーを毎月生成</div>
            </div>
            <div className={SAMPLE_THUMBS}>
              {SAMPLE_BANNERS.map((src, i) => (
                <img className={SAMPLE_THUMB} key={i} src={src} alt={`バナー生成例 ${i + 1}`} loading="lazy" />
              ))}
            </div>
            <div className={PRICE_ROW}>
              <span className={PRICE}>{plan.price}</span>
              <span className={PRICE_SUFFIX}>/月</span>
            </div>
            <ul className={FEATURE_LIST}>
              {plan.features.map((feature, i) => (
                <li className={FEATURE_ITEM} key={i}>{feature}</li>
              ))}
            </ul>
            <button
              className={trialButtonClass(plan.recommended, loadingPlanId === plan.id)}
              type="button"
              disabled={loadingPlanId !== null}
              onClick={() => handleSelectPlan(plan)}
            >
              {loadingPlanId === plan.id ? '処理中...' : '7日間無料で始める'}
            </button>
            <div className={GUARANTEE_NOTE}>今日は¥0・いつでも解約できます</div>
          </div>
        ))}
      </div>

      <div className={TRIAL_INFO}>
        <span className={SHIELD_ICON}>
          <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </span>
        7日間の無料トライアル付き
      </div>

      <button className={SKIP_LINK} type="button" onClick={onSkip}>
        後で登録する
      </button>

      <div className={FOOTER_LINKS}>
        <a className={FOOTER_LINK} href="https://tasky-ai.com/help/cancel" target="_blank" rel="noopener noreferrer">
          解約方法はこちら
        </a>
      </div>
    </div>
  );
};

export default PlanSelectionStep;
